import { useMemo } from 'react';

import { Box } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { useExport } from '@/features/DataHub/modules/Export/context/ExportContext';
import { DateRangeType } from '@/features/DataHub/modules/Export/types';

type DateRangePickersProps = {
  dateFormat: string;
  from: Date | null;
  to: Date | null;
  minDate?: Date;
  maxDate?: Date;
  fromError?: boolean;
  fromErrorMessage?: string;
  toError?: boolean;
  toErrorMessage?: string;
  onChange: (field: 'from' | 'to', value: Date | null) => void;
};

const textFieldSx = {
  '.MuiPickersInputBase-root': {
    fontSize: '14px',
    width: '100%',
    flex: 1
  },
  '.MuiPickersInputBase-sectionsContainer': {
    py: 1
  },
  '.MuiPickersInputBase-root::before': {
    display: 'none'
  }
};

const minOfDates = (a?: Date | null, b?: Date | null): Date | undefined => {
  if (!a) return b ?? undefined;
  if (!b) return a;
  return a < b ? a : b;
};

const maxOfDates = (a?: Date | null, b?: Date | null): Date | undefined => {
  if (!a) return b ?? undefined;
  if (!b) return a;
  return a > b ? a : b;
};

export const DateRangePickers = ({
  dateFormat,
  from,
  to,
  minDate,
  maxDate,
  fromError,
  fromErrorMessage,
  toError,
  toErrorMessage,
  onChange
}: DateRangePickersProps) => {
  const placeholder = useMemo(() => dateFormat.toUpperCase(), [dateFormat]);
  const { exportSettings } = useExport();
  const isDatePickerDisabled = exportSettings.dateRangeType !== DateRangeType.DATES;

  const fromMaxDate = minOfDates(maxDate, to);
  const toMinDate = maxOfDates(minDate, from);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        mt: 1,
        gap: 1,
        '.MuiFormControl-root': { flex: 1 }
      }}
    >
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        localeText={{
          fieldMonthPlaceholder: ({ format }) => format
        }}
      >
        <DatePicker
          disabled={isDatePickerDisabled}
          format={dateFormat}
          maxDate={fromMaxDate}
          minDate={minDate}
          slotProps={{
            openPickerIcon: { fontSize: 'small' },
            textField: {
              focused: false,
              size: 'small',
              error: fromError,
              helperText: fromErrorMessage,
              placeholder,
              variant: 'filled',
              sx: textFieldSx
            }
          }}
          value={from}
          onChange={value => {
            onChange('from', value);
          }}
        />
        <DatePicker
          disabled={isDatePickerDisabled}
          format={dateFormat}
          maxDate={maxDate}
          minDate={toMinDate}
          slotProps={{
            openPickerIcon: { fontSize: 'small' },
            textField: {
              focused: false,
              size: 'small',
              error: toError,
              helperText: toErrorMessage,
              placeholder,
              variant: 'filled',
              sx: textFieldSx
            }
          }}
          value={to}
          onChange={value => {
            onChange('to', value);
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};
